import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { AdsPlacement } from 'types/enums/AdsPlacement';
import { InterstitialAds } from 'types/interfaces/ads/InterstitialAds';

import { TrackingApi } from 'api/TrackingApi';
import { setLocalStorageItem } from 'helpers/localStorage';
import { useDynamicBannerAnalytics } from 'hooks/analytics/useDynamicBannerAnalytics';
import { useInterstitialPopupAnalytics } from 'hooks/analytics/useInterstitialPopupAnalytics';
import { useAdsQuery } from 'hooks/queries/useAdsQuery';
import { useAdsPlatform } from 'hooks/useAdsPlatform';
import { useBeforeUnload } from 'hooks/useBeforeUnload';
import { IS_QUALIFICATION_SET_STORAGE_KEY } from 'hooks/useInterstitialPopup';

import { AdsCreative } from 'components/shared/AdsCreative';
import { FullscreenSpinner } from 'components/shared/FullscreenSpinner';
import { Popup } from 'components/UI/Popup';

import styles from './interstitialPopup.module.sass';

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

interface Placement {
  src: null | string;
  href: null | string;
}

export const InterstitialPopup: React.FC<Props> = ({ onAction }) => {
  const { t } = useTranslation();

  const [trackData, setTrackData] = useState<undefined | InterstitialAds>();
  const [placement, setPlacement] = useState<Placement>({
    src: null,
    href: null,
  });

  const {
    trackInterstitialPopupShow,
    trackInterstitialPopupClose,
    trackInterstitialPopupClick,
  } = useInterstitialPopupAnalytics();

  const { getAdsPlatform } = useAdsPlatform();
  const { trackBannerLoad } = useDynamicBannerAnalytics();

  const {
    adsQuery: { isLoading },
  } = useAdsQuery(
    {
      placement: AdsPlacement.Interstitial,
      platform: getAdsPlatform(),
    },
    {
      onSuccess: (data: InterstitialAds) => {
        if (!data?.url || !data?.image_url) {
          TrackingApi.trackError({
            type: 'error',
            message: `fetchInterstitialBanner crash, didn't get url or image_url, url: ${data?.url}, image_url: ${data?.image_url}`,
          });
        } else {
          setTrackData(data);

          setPlacement({
            src: data.image_url,
            href: data.url,
          });
        }
      },
      onError: (error: any) => {
        if (error?.response?.status === 403) {
          setLocalStorageItem(IS_QUALIFICATION_SET_STORAGE_KEY, null);
        }
      },
      retry: 3,
    }
  );

  useBeforeUnload({
    beforeUnload: () => {
      trackInterstitialPopupClose();
    },
  });

  const handleClose = useCallback(() => {
    onAction({ type: 'close' });
  }, [onAction]);

  const handleClickSkip = (e: MouseEvent<HTMLButtonElement>) => {
    trackInterstitialPopupClick('skip');

    handleClose();

    e.stopPropagation();
  };

  const handleClick = () => {
    trackInterstitialPopupClick('click');

    handleClose();
  };

  const handleLoad = () => {
    if (!trackData) return;

    trackBannerLoad({
      banner: trackData,
      requestTs: Date.now(),
    });
  };

  const handlePopupOverlayClick = () => {
    if (placement.href) {
      handleClick();
      window.open(placement.href, '_blank');
    }
  };

  useEffect(() => {
    if (placement.href && placement.src) trackInterstitialPopupShow();
  }, [placement.href, placement.src, trackInterstitialPopupShow]);

  useEffect(() => {
    if (!isLoading && (!placement.href || !placement.src)) {
      handleClose();
    }
  }, [isLoading, handleClose, placement.href, placement.src]);

  if (isLoading) {
    return <FullscreenSpinner />;
  }

  if (!placement.href || !placement.src) {
    return null;
  }

  return (
    <Popup
      rootClassName={styles.rootPopupClass}
      bodyClassName={styles.bodyPopupClass}
      popupClassName={styles.popup}
      onOverlayClick={handlePopupOverlayClick}
    >
      <button
        className={styles.skipBtn}
        onClick={handleClickSkip}
        type="button"
      >
        {t('common:skip')}
      </button>
      <AdsCreative
        href={placement.href}
        src={placement.src}
        creativeClassName={styles.creativeClass}
        onClick={handleClick}
        onLoad={handleLoad}
      />
    </Popup>
  );
};
